// Pure display-logic helpers for the Audience Feedback Intelligence UI (prompt 150). Kept
// framework-free and unit-testable, same pattern as moduleModels.ts.
//
// Copy rules (spec `12_PRIVACY_PLATFORM_LIMITS.md` §Product language rules / `02_*` §Copy
// rules): say "confidence: low/medium/high", "evidence suggests", "mapped to beat/bar/phrase by
// DJSEM" — never "AI knows", "proves", "native beat comments", or "objective best track". These
// helpers are the single place that copy gets generated, so the rule is enforced in code, not
// just documentation.

/** Confidence tier shown to the DJ — never a bare number without this label alongside it. */
export type ConfidenceTier = "low" | "medium" | "high";

/**
 * Buckets a raw 0..1 confidence into the low/medium/high tier the UI is required to show
 * alongside every score (spec: "confidence: low / medium / high").
 * @param confidence - raw 0..1 confidence value.
 * @returns the {@link ConfidenceTier}.
 */
export function confidenceTier(confidence: number): ConfidenceTier {
  if (confidence >= 0.70) return "high";
  if (confidence >= 0.40) return "medium";
  return "low";
}

/** Badge tone for a confidence tier, reused by every dashboard panel. */
export function confidenceTone(confidence: number): "ok" | "neutral" | "warn" {
  const tier = confidenceTier(confidence);
  return tier === "high" ? "ok" : tier === "medium" ? "neutral" : "warn";
}

const RECOMMENDATION_LABELS: Record<string, string> = {
  reuse: "Reuse in future sets",
  move_earlier: "Try earlier in the set",
  move_later: "Try later in the set",
  shorten: "Consider shortening",
  replace: "Evidence suggests replacing",
  improve_transition: "Improve this transition",
  highlight_in_future_sets: "Highlight in future sets",
  needs_more_data: "Needs more data",
};

/**
 * Human-readable label for a `SetLearningRecommendation` value, following the spec's
 * evidence-based, non-absolute phrasing.
 * @param recommendation - the raw recommendation value.
 * @returns the display label, or the raw value title-cased if unrecognized.
 */
export function recommendationLabel(recommendation: string): string {
  return RECOMMENDATION_LABELS[recommendation] ?? recommendation.replace(/_/g, " ");
}

/**
 * The one-line evidence-framed summary shown under a score — always "evidence suggests",
 * never "AI knows"/"proves" (guardrail copy rule).
 * @param evidenceCount - how many signals contributed to this score.
 * @param confidence - raw 0..1 confidence value.
 * @returns the summary line.
 */
export function evidenceSummaryLine(evidenceCount: number, confidence: number): string {
  if (evidenceCount === 0) return "No evidence yet — needs more data.";
  const tier = confidenceTier(confidence);
  return `Evidence suggests this, based on ${evidenceCount} signal${evidenceCount === 1 ? "" : "s"} (confidence: ${tier}).`;
}

/** Sort key: strongest, most-confident scores first — used by the Track/Phrase/Transition dashboards. */
export function byStrongestFirst<T extends { combinedScore: number; confidence: number }>(a: T, b: T): number {
  if (b.confidence !== a.confidence) return b.confidence - a.confidence;
  return b.combinedScore - a.combinedScore;
}

/**
 * Formats a track-local millisecond offset as `M:SS`, used to display SoundCloud
 * `timestampMs`/YouTube `videoTimeMs` values on the timeline overlays.
 * @param ms - milliseconds from track/video start.
 * @returns the `M:SS` formatted string.
 */
export function formatTimestampMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Sentiment badge tone — kept in one place so every panel colors sentiment identically. */
export function sentimentTone(sentiment: string): "ok" | "neutral" | "warn" {
  if (sentiment === "positive") return "ok";
  if (sentiment === "negative") return "warn";
  return "neutral";
}
